import { Link } from 'react-router-dom';
import { Check, CheckCircle2, ChevronRight, Lock, Play } from 'lucide-react';
import type { CourseLesson } from '@/lib/api';
import { fmtSeconds } from '@/lib/utils';

/**
 * One lesson in a lesson list: thumb (or icon fallback), numbered title, a
 * duration / "% gesehen" / locked-reason line, and a trailing state icon.
 *
 * A locked row keeps the same anatomy but is deliberately not a link — it's an
 * inert, dashed placeholder that tells you why it's shut.
 *
 * `number` is the 1-based position shown before the title. It's passed in
 * rather than derived, because the numbering is per module and the row doesn't
 * know which list it's in.
 */
export function LessonRow({
  lesson,
  number,
  locked,
  to,
}: {
  lesson: CourseLesson;
  number: number;
  locked: boolean;
  to: string;
}) {
  const completed = lesson.progress.completed;

  const inner = (
    <>
      {lesson.thumbnailUrl ? (
        <img
          src={lesson.thumbnailUrl}
          alt=""
          className={
            locked
              ? 'h-14 w-20 shrink-0 rounded-md object-cover opacity-50 grayscale'
              : 'h-14 w-20 shrink-0 rounded-md object-cover'
          }
        />
      ) : (
        <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-md bg-muted">
          {locked ? (
            <Lock className="size-4 text-muted-foreground" />
          ) : completed ? (
            <Check className="size-5 text-primary-strong" />
          ) : (
            <Play className="size-4 text-muted-foreground" />
          )}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div
          className={
            locked
              ? 'line-clamp-2 text-sm font-medium leading-snug text-muted-foreground'
              : 'line-clamp-2 text-sm font-medium leading-snug text-foreground'
          }
        >
          {number}. {lesson.title}
        </div>
        <div className="text-xs text-muted-foreground">
          {locked ? (
            'Vorherige Lektion zuerst abschließen'
          ) : (
            <>
              {lesson.durationSeconds > 0 ? fmtSeconds(lesson.durationSeconds) : '–'}
              {lesson.progress.furthestSeconds > 0 && !completed && (
                <>
                  {' · '}
                  {Math.min(
                    100,
                    Math.round(
                      (lesson.progress.furthestSeconds / Math.max(lesson.durationSeconds, 1)) * 100,
                    ),
                  )}
                  % gesehen
                </>
              )}
            </>
          )}
        </div>
      </div>
      {locked ? (
        <Lock className="size-4 shrink-0 text-muted-foreground" />
      ) : completed ? (
        <CheckCircle2 className="size-5 shrink-0 text-success" />
      ) : (
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      )}
    </>
  );

  if (locked) {
    return (
      <div
        aria-disabled
        title="Schließe zuerst die vorherige Lektion ab."
        className="flex cursor-not-allowed items-center gap-4 rounded-xl border border-dashed bg-muted/40 p-4"
      >
        {inner}
      </div>
    );
  }
  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-xl border bg-card p-4 text-card-foreground shadow-soft transition duration-200 hover:border-primary/40 hover:shadow-lift"
    >
      {inner}
    </Link>
  );
}
